import React, { useState, useEffect } from "react";
import { getFirestore, query, collection, getDocs } from "firebase/firestore";
import app from "../firebaseConfig";
import VehicleCard from "./VehicleCard";
import VehicleCardAlt from "./VehicleCardAlt";

import Slider from "@mui/material/Slider";
import SearchIcon from "@mui/icons-material/Search";

// Import the back7 image
import back7 from "./back8.png"; // Adjust path as necessary

const db = getFirestore(app);

const CarListingPage = () => {
  // Responsive state for mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const [cars, setCars] = useState([]);
  const [filteredCars, setFilteredCars] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid"); // "grid" or "list"
  const [showFilter, setShowFilter] = useState(false);

  // Filters object
  const [filters, setFilters] = useState({
    carType: "",
    valueRange: [0, 1000000],
    roiRange: [0, 50],
  });

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const q = query(collection(db, "cars"));
        const snap = await getDocs(q);
        const fetched = snap.docs.map((doc) => ({
          carID: doc.id,
          ...doc.data(),
        }));

        setCars(fetched);
        // Immediately apply filters to ensure fully-funded cars go to bottom
        const initialFiltered = applyAllFilters(fetched, filters, "");
        setFilteredCars(initialFiltered);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching cars:", error);
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const separateFullyFunded = (list) => {
    const notFullyFunded = [];
    const fullyFunded = [];

    list.forEach((car) => {
      const moneyRaised = Number(car.moneyRaised) || 0;
      const totalValue = Number(car.totalValue) || 1;
      const progress = (moneyRaised / totalValue) * 100;

      if (progress >= 100) {
        fullyFunded.push(car);
      } else {
        notFullyFunded.push(car);
      }
    });

    return [...notFullyFunded, ...fullyFunded];
  };

  const applyAllFilters = (carsList, filterObject, searchText) => {
    let temp = [...carsList];

    // 1. Search by carName (case-insensitive)
    const searchTrim = searchText.trim().toLowerCase();
    if (searchTrim) {
      temp = temp.filter((car) =>
        (car.carName || "").toLowerCase().includes(searchTrim)
      );
    }

    // 2. Car Type (exact match, ignoring case)
    if (filterObject.carType.trim()) {
      const desiredType = filterObject.carType.toLowerCase();
      temp = temp.filter((car) => {
        const ctype = (car.carType || "").toLowerCase().trim();
        return ctype === desiredType;
      });
    }

    // 3. Car Value Range
    const [minVal, maxVal] = filterObject.valueRange;
    temp = temp.filter((car) => {
      const val = Number(car.totalValue) || 0;
      return val >= minVal && val <= maxVal;
    });

    // 4. ROI Range
    const [minROI, maxROI] = filterObject.roiRange;
    temp = temp.filter((car) => {
      const roi = Number(car.averageROI) || 0;
      return roi >= minROI && roi <= maxROI;
    });

    // 5. Sort out fully-funded cars to bottom
    const finalList = separateFullyFunded(temp);
    return finalList;
  };

  const handleSearch = (e) => {
    const newSearch = e.target.value;
    setSearch(newSearch);

    // Re-apply filters with updated search
    const finalList = applyAllFilters(cars, filters, newSearch);
    setFilteredCars(finalList);
  };

  const handleFilterChange = (key, newValue) => {
    const updated = { ...filters, [key]: newValue };
    setFilters(updated);

    // Re-apply filters with updated filter + current search
    const finalList = applyAllFilters(cars, updated, search);
    setFilteredCars(finalList);
  };

  const handleROISliderChange = (event, newValue) => {
    handleFilterChange("roiRange", newValue);
  };

  const handleValueSliderChange = (event, newValue) => {
    handleFilterChange("valueRange", newValue);
  };

  return (
    <div
      style={{
        backgroundImage: `url(${back7})`,
        backgroundSize: "contain",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "top",
        minHeight: "100vh",
        margin: 0,
      }}
    >
      <div style={{ padding: "20px", fontFamily: "Arial, sans-serif", color: "#fff", marginBottom: "00px" }}>
        {/* Header */}
        <h1
          style={{
            textAlign: "center",
            fontSize: isMobile ? "2rem" : "5rem",
            fontWeight: "bold",
            marginTop: isMobile ? "40px" : "100px",
            color: "white",
          }}
        >
          Turning Vehicles Into Assets
        </h1>

        <p
          style={{
            textAlign: "center",
            fontSize: isMobile ? "0.5rem" : "1.2rem",
            marginBottom: "25px",
            color: "#fff",
            marginTop: isMobile ? "10px" : "125px",
          }}
        >
          Earn revenue, get discounts and use it when you need it
        </p>

        {/* Search & Buttons */}
        <div
          style={{
            marginBottom: isMobile ? "15px" : "50px",
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            flexWrap: isMobile ? "nowrap" : "wrap",
          }}
        >
          <input
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder="Find your next investment vehicle..."
            style={{
              width: isMobile ? "80%" : "700px",
              padding: "10px",
              fontSize: "1.2em",
              border: "1px solid #ccc",
              borderRadius: "5px",
              marginBottom: 0,
            }}
          />
          <button
            onClick={() => setShowFilter(!showFilter)}
            style={{
              padding: "10px",
              fontSize: "1.2em",
              borderRadius: "5px",
              border: "1px solid #ccc",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "#fff",
            }}
          >
            <SearchIcon />
          </button>
          <button
            onClick={() => setViewMode(viewMode === "list" ? "grid" : "list")}
            style={{
              padding: "10px",
              fontSize: "1.2em",
              borderRadius: "5px",
              border: "1px solid #ccc",
              cursor: "pointer",
              backgroundColor: "#007bff",
              color: "#fff",
            }}
          >
            {viewMode === "list" ? "☰" : "☷"}
          </button>
        </div>

        {/* Filter Box */}
        {showFilter && (
          <div
            style={{
              backgroundColor: "#1e1e1e",
              padding: "10px",
              borderRadius: "5px",
              marginBottom: "10px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.2)",
              display: "flex",
              alignItems: "center",
              gap: "20px",
              justifyContent: "center",
              maxWidth: "800px",
              margin: "0 auto",
            }}
          >
            <h3 style={{ color: "white", margin: 0 }}>Filters</h3>

            {/* Car Type */}
            <select
              name="carType"
              value={filters.carType}
              onChange={(e) => handleFilterChange("carType", e.target.value)}
              style={{
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                minWidth: "150px",
                marginRight: "30px",
              }}
            >
              <option value="">All</option>
              <option value="commuter vehicle">Commuter Vehicle</option>
              <option value="supercar">SuperCar</option>
              <option value="hypercar">HyperCar</option>
              <option value="truck">Truck</option>
              <option value="rv">RV</option>
              <option value="special vehicle">Special Vehicle</option>
            </select>

            {/* Car Value Range */}
            <div style={{ minWidth: "200px", marginRight: "10px" }}>
              <label
                style={{
                  fontSize: "1em",
                  textAlign: "center",
                  marginLeft: "65px",
                  color: "#fff",
                }}
              >
                Car Value
              </label>
              <Slider
                value={filters.valueRange}
                onChange={handleValueSliderChange}
                valueLabelDisplay="auto"
                min={0}
                max={1000000}
                style={{ color: "#32B704" }}
              />
              <p style={{ fontSize: "0.9em", textAlign: "center" }}>
                ${filters.valueRange[0].toLocaleString()} - $
                {filters.valueRange[1].toLocaleString()}
              </p>
            </div>

            {/* ROI Range with onChangeCommitted */}
            <div style={{ minWidth: "200px" }}>
              <label
                style={{
                  fontSize: "1em",
                  textAlign: "center",
                  marginLeft: "85px",
                  color: "#fff",
                }}
              >
                ROI
              </label>
              <Slider
                value={filters.roiRange}
                onChangeCommitted={handleROISliderChange}
                onChange={(e, newValue) => {
                  // visually update slider while dragging
                  setFilters((prev) => ({ ...prev, roiRange: newValue }));
                }}
                valueLabelDisplay="auto"
                min={0}
                max={50}
                style={{ color: "#32B704" }}
              />
              <p style={{ fontSize: "0.9em", textAlign: "center" }}>
                {filters.roiRange[0]}% - {filters.roiRange[1]}%
              </p>
            </div>
          </div>
        )}

        {/* Cars Section */}
        {loading ? (
          <p style={{ textAlign: "center" }}>Loading vehicles...</p>
        ) : filteredCars.length > 0 ? (
          viewMode === "grid" ? (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
              }}
            >
              {filteredCars.map((car) => {
                const moneyRaised = Number(car.moneyRaised) || 0;
                const totalValue = Number(car.totalValue) || 1;
                const progress = (moneyRaised / totalValue) * 100;
                return (
                  <VehicleCard
                    key={car.carID}
                    carID={car.carID}
                    carName={car.carName}
                    tags={car.tags || []}
                    image={
                      car.pictures
                        ? car.pictures[0]
                        : "https://via.placeholder.com/300"
                    }
                    totalReceived={car.moneyRaised}
                    roi={car.averageROI}
                    totalValue={car.totalValue}
                    location={car.location}
                    progress={progress}
                  />
                );
              })}
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
              {filteredCars.map((car) => {
                const moneyRaised = Number(car.moneyRaised) || 0;
                const totalValue = Number(car.totalValue) || 1;
                const progress = (moneyRaised / totalValue) * 100;
                return (
                  <VehicleCardAlt
                    key={car.carID}
                    carID={car.carID}
                    carName={car.carName}
                    tags={car.tags || []}
                    image={
                      car.pictures
                        ? car.pictures[0]
                        : "https://via.placeholder.com/300"
                    }
                    totalReceived={car.moneyRaised}
                    roi={car.averageROI}
                    totalValue={car.totalValue}
                    location={car.location}
                    progress={progress}
                  />
                );
              })}
            </div>
          )
        ) : (
          <p style={{ textAlign: "center", color: "#777" }}>
            No vehicles match your search.
          </p>
        )}
      </div>
    </div>
  );
};

export default CarListingPage;
